import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useNewsApiSettings } from '../../hooks/useNewsApiSettings'
import { supabase } from '../../services/supabase'

const NEWS_API_KEYS_FUNCTION = 'manage-news-api-keys'
const FETCH_SUPPLIER_NEWS_FUNCTION = 'fetch-supplier-news'

const PROVIDERS: { id: string; name: string }[] = [
  { id: 'gnews', name: 'GNews' },
  { id: 'newsapi_org', name: 'NewsAPI.org' },
]

export default function NewsApiPanel() {
  const { isAdmin, companyId } = useAuth()
  const { settings, loading, refetch, upsertSetting } = useNewsApiSettings()
  const [apiKeyInputs, setApiKeyInputs] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [refreshLoading, setRefreshLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 5000)
      return () => clearTimeout(t)
    }
  }, [message])

  const handleSaveProvider = async (providerId: string, enabled: boolean) => {
    if (!isAdmin) return
    setSaving(providerId)
    setMessage(null)
    try {
      await upsertSetting({ provider: providerId, enabled, config: {} })
      const keyInput = apiKeyInputs[providerId]?.trim()
      if (keyInput) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(NEWS_API_KEYS_FUNCTION, {
          body: { provider: providerId, apiKey: keyInput },
        })
        if (fnError) throw fnError
        if (fnData?.error) throw new Error(fnData.error)
        setApiKeyInputs((prev) => ({ ...prev, [providerId]: '' }))
      }
      await refetch()
      setMessage({ type: 'success', text: `${providerId} saved.` })
    } catch (e: unknown) {
      const err = e instanceof Error ? e : new Error(String(e))
      setMessage({ type: 'error', text: err.message })
    } finally {
      setSaving(null)
    }
  }

  const handleRefreshNews = async () => {
    if (!isAdmin) return
    setRefreshLoading(true)
    setMessage(null)
    try {
      const { data, error } = await supabase.functions.invoke(FETCH_SUPPLIER_NEWS_FUNCTION, {
        body: { companyId: companyId ?? undefined },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      setMessage({ type: 'success', text: data?.message ?? 'News refresh started.' })
    } catch (e: unknown) {
      const err = e instanceof Error ? e : new Error(String(e))
      setMessage({ type: 'error', text: err.message })
    } finally {
      setRefreshLoading(false)
    }
  }

  if (!isAdmin) return null

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-lg font-bold">
            <i className="fa-solid fa-newspaper mr-2" aria-hidden />
            News API
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure news providers for Early Signals. API keys are stored securely and never exposed to the client.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefreshNews}
          disabled={refreshLoading}
          className="px-4 py-2 bg-black text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
        >
          {refreshLoading ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
              Refreshing...
            </>
          ) : (
            <>
              <i className="fa-solid fa-arrow-rotate-right" aria-hidden />
              Refresh news
            </>
          )}
        </button>
      </div>

      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
          Loading...
        </div>
      ) : (
        <div className="space-y-4">
          {PROVIDERS.map((provider) => {
            const row = settings.find((s) => s.provider === provider.id)
            const enabled = row?.enabled ?? false
            return (
              <div
                key={provider.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50/50"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-semibold">{provider.name}</h3>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => handleSaveProvider(provider.id, e.target.checked)}
                      disabled={saving === provider.id}
                    />
                    <span className="text-sm">Enabled</span>
                  </label>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    type="password"
                    value={apiKeyInputs[provider.id] ?? ''}
                    onChange={(e) =>
                      setApiKeyInputs((prev) => ({ ...prev, [provider.id]: e.target.value }))
                    }
                    placeholder="Leave blank to keep existing key"
                    className="w-full max-w-md p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleSaveProvider(provider.id, enabled)}
                  disabled={saving === provider.id}
                  className="mt-3 px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                >
                  {saving === provider.id ? 'Saving...' : 'Save'}
                </button>
              </div>
            )
          })}
        </div>
      )}

      <p className="text-xs text-gray-500">
        GNews: free tier at gnews.io. NewsAPI.org: add key at newsapi.org. &quot;Refresh news&quot; fetches articles and saves them to Early Signals.
      </p>
    </div>
  )
}
